import { useState, useRef } from "react";
import { C, fmt, fmtK, getDaysInMonth, thisMonthStr, todayStr } from "./constants";
import { Label } from "./ui";

function SpendingChart({ transactions, month }) {
  const days = getDaysInMonth(month);
  const today = new Date();
  const isCurrentMonth = month === thisMonthStr();
  const todayDay = isCurrentMonth ? today.getDate() : days;

  // Build daily cumulative spending
  const daily = Array(days).fill(0);
  transactions
    .filter(t => t.date.startsWith(month) && t.amount < 0)
    .forEach(t => {
      const d = parseInt(t.date.slice(8,10)) - 1;
      if (d >= 0 && d < days) daily[d] += Math.abs(t.amount);
    });

  // Cumulative
  const cumulative = daily.reduce((acc, v, i) => { acc.push((acc[i-1]||0) + v); return acc; }, []);
  const maxVal = Math.max(...cumulative, 1);

  const W = 320, H = 80;
  const pts = cumulative.map((v, i) => {
    const x = (i / (days-1)) * (W - 16) + 8;
    const y = H - 8 - (v / maxVal) * (H - 16);
    return `${x},${y}`;
  }).join(" ");

  // Tooltip state
  const [hover, setHover] = useState(null);
  const svgRef = useRef();

  const handleMouseMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const dayIdx = Math.round(((x - 8) / (W - 16)) * (days - 1));
    if (dayIdx >= 0 && dayIdx < days) setHover(dayIdx);
  };

  return (
    <div style={{ position:"relative" }} onMouseLeave={() => setHover(null)}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:80, cursor:"crosshair" }}
        onMouseMove={handleMouseMove}>
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.purple} stopOpacity="0.3"/>
            <stop offset="100%" stopColor={C.purple} stopOpacity="0"/>
          </linearGradient>
        </defs>
        {/* Fill area */}
        <polygon key={"fill-"+month} points={`8,${H} ${pts} ${(days-1)/(days-1)*(W-16)+8},${H}`} fill="url(#chartGrad)"
          style={{ opacity:0, animation:"fadeUp 0.8s ease 0.3s forwards" }}/>
        {/* Line */}
        <polyline key={month} points={pts} fill="none" stroke={C.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ opacity:0, animation:"fadeIn 0.6s ease 0.1s forwards" }}/>
        {/* Today dot */}
        {isCurrentMonth && (() => {
          const x = ((todayDay-1)/(days-1))*(W-16)+8;
          const y = H - 8 - ((cumulative[todayDay-1]||0)/maxVal)*(H-16);
          return <circle cx={x} cy={y} r={4} fill={C.purple} />;
        })()}
        {/* Hover line */}
        {hover !== null && (() => {
          const x = (hover/(days-1))*(W-16)+8;
          return <line x1={x} y1={8} x2={x} y2={H-2} stroke={C.border2} strokeWidth={1} strokeDasharray="3,3"/>;
        })()}
      </svg>
      {hover !== null && (
        <div style={{ position:"absolute", top: 0, left: Math.min(hover/(days-1)*100, 70)+"%",
          background: C.surface3, border: `1px solid ${C.border2}`, borderRadius: 8,
          padding: "6px 10px", fontSize: 11, color: C.text, pointerEvents:"none", whiteSpace:"nowrap", zIndex:10 }}>
          <div style={{ color: C.text2, marginBottom: 2 }}>Day {hover+1}</div>
          <div style={{ color: C.purple, fontWeight: 700 }}>{fmt(cumulative[hover]||0)} spent</div>
        </div>
      )}
    </div>
  );
}

// ── CALENDAR VIEW ─────────────────────────────────────────────
function CalendarView({ transactions, month, onDayClick, selectedDay }) {
  const days = getDaysInMonth(month);
  const [y, m] = month.split("-").map(Number);
  const firstDow = new Date(y, m-1, 1).getDay();

  const dayTotals = {};
  transactions.filter(t => t.date.startsWith(month)).forEach(t => {
    const d = parseInt(t.date.slice(8,10));
    if (!dayTotals[d]) dayTotals[d] = { income:0, expense:0 };
    if (t.amount > 0) dayTotals[d].income += t.amount;
    else dayTotals[d].expense += Math.abs(t.amount);
  });

  // Average daily expense for high-spend highlighting
  const expenseDays = Object.values(dayTotals).filter(d => d.expense > 0);
  const avgExpense = expenseDays.length > 0
    ? expenseDays.reduce((s, d) => s + d.expense, 0) / expenseDays.length
    : 0;

  const todayFull = todayStr();
  const todayDay = todayFull.startsWith(month) ? parseInt(todayFull.slice(8,10)) : null;

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  const DOW = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:8 }}>
        {DOW.map(d => (
          <div key={d} style={{ textAlign:"center", fontSize:10, color:C.text3, fontWeight:600, padding:"4px 0", letterSpacing:"0.05em" }}>{d}</div>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e${idx}`} />;
          const tot = dayTotals[day];
          const isToday = day === todayDay;
          const isSel = day === selectedDay;
          const isHighSpend = tot && avgExpense > 0 && tot.expense > avgExpense * 1.5;

          return (
            <div key={day} onClick={() => onDayClick(day)}
              style={{
                borderRadius:10, padding:"8px 4px 6px", cursor:"pointer", textAlign:"center",
                background: isSel
                  ? C.purple
                  : isToday
                    ? "rgba(167,139,250,0.15)"
                    : isHighSpend
                      ? "rgba(240,82,82,0.08)"
                      : tot ? C.surface2 : "transparent",
                border: `1px solid ${isSel ? C.purple : isToday ? "rgba(167,139,250,0.35)" : "transparent"}`,
                transition:"all 0.15s", minHeight:64,
                display:"flex", flexDirection:"column", alignItems:"center", gap:5,
              }}>
              <div style={{
                fontSize:13, fontWeight:600, lineHeight:1,
                color: isSel ? "#0e0e13" : isToday ? C.purple : C.text,
              }}>{day}</div>
              <div style={{ display:"flex", gap:3, justifyContent:"center", minHeight:6 }}>
                {tot?.income  > 0 && <div style={{ width:5, height:5, borderRadius:"50%", background: isSel ? "#0e0e13" : C.green }} />}
                {tot?.expense > 0 && <div style={{ width:5, height:5, borderRadius:"50%", background: isSel ? "#0e0e13" : C.red  }} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── WEEKLY LINE CHART ────────────────────────────────────────
function WeeklyChart({ transactions }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef();

  // Build Mon–Sun using timestamp math from local midnight
  const WEEK_LABELS = ["Mo","Tu","We","Th","Fr","Sa","Su"];
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayStr_ = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  // getDay(): 0=Sun,1=Mon,...,6=Sat → offset to Monday: Sun→-6, Mon→0, Tue→-1, ..., Sat→-5
  const dow = todayMidnight.getDay();
  const offsetToMon = dow === 0 ? -6 : 1 - dow;
  const MS = 86400000;

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(todayMidnight.getTime() + (offsetToMon + i) * MS);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const isFuture = dateStr > todayStr_;
    const spent = isFuture ? 0 : transactions
      .filter(t => t.date === dateStr && t.amount < 0)
      .reduce((s, t) => s + Math.abs(t.amount), 0);
    days.push({ dateStr, label: WEEK_LABELS[i], spent, isToday: dateStr === todayStr_, isFuture });
  }
  console.log("[WeeklyChart] week days:", days.map(d => `${d.label}=${d.dateStr}${d.isToday?" TODAY":""}${d.isFuture?" future":""}`));

  const vals = days.map(d => d.spent);
  const maxVal = Math.max(...vals, 1);

  const W = 320, H = 80;
  // Use a raised floor (yFloor) so zero days sit visibly above the baseline
  const yTop = 10, yFloor = H - 14;
  const getY = (v) => v === 0 ? yFloor : yTop + (1 - v / maxVal) * (yFloor - yTop);
  const getX = (i) => (i / (days.length - 1)) * (W - 16) + 8;

  const pts = vals.map((v, i) => `${getX(i)},${getY(v)}`).join(" ");

  const handleMouseMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const idx = Math.round(((x - 8) / (W - 16)) * (days.length - 1));
    if (idx >= 0 && idx < days.length) setHover(idx);
  };

  // Today is always the last non-future day (Sun = index 6)
  const todayIdx = days.findIndex(d => d.isToday);
  const todayX = todayIdx >= 0 ? getX(todayIdx) : getX(days.length - 1);
  const todayY = todayIdx >= 0 ? getY(vals[todayIdx]) : yFloor;

  return (
    <div style={{ position:"relative" }} onMouseLeave={() => setHover(null)}>
      {/* Baseline — faint line showing Mon→Sun floor */}
      <div style={{ position:"absolute", left:8, right:8, bottom:20, height:1,
        background:"rgba(255,255,255,0.05)", borderRadius:1 }} />
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:80, cursor:"crosshair" }}
        onMouseMove={handleMouseMove}>
        <defs>
          <linearGradient id="weekGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.purple} stopOpacity="0.25"/>
            <stop offset="100%" stopColor={C.purple} stopOpacity="0"/>
          </linearGradient>
        </defs>
        {/* Fill */}
        <polygon points={`${getX(0)},${H} ${pts} ${getX(days.length-1)},${H}`}
          fill="url(#weekGrad)" style={{ opacity:0, animation:"fadeUp 0.8s ease 0.3s forwards" }}/>
        {/* Line */}
        <polyline points={pts} fill="none" stroke={C.purple} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ opacity:0, animation:"fadeIn 0.6s ease 0.1s forwards" }}/>
        {/* Day dots */}
        {vals.map((v, i) => {
          const x = getX(i);
          const y = getY(v);
          const isToday = days[i].isToday;
          const isFuture = days[i].isFuture;
          return <circle key={i} cx={x} cy={y} r={isToday ? 4 : 2.5}
            fill={isFuture ? "rgba(255,255,255,0.08)" : isToday ? C.purple : v > 0 ? C.purple : "rgba(167,139,250,0.55)"} />;
        })}
        {/* Hover line */}
        {hover !== null && (() => {
          const x = getX(hover);
          return <line x1={x} y1={8} x2={x} y2={H - 2} stroke={C.border2} strokeWidth={1} strokeDasharray="3,3"/>;
        })()}
      </svg>
      {/* Day labels */}
      <div style={{ display:"flex", justifyContent:"space-between", padding:"0 8px", marginTop:4 }}>
        {days.map((d, i) => (
          <span key={i} style={{ fontSize:10, color: d.isToday ? C.purple : C.text3, fontWeight: d.isToday ? 700 : 400 }}>
            {d.label}
          </span>
        ))}
      </div>
      {/* Hover tooltip */}
      {hover !== null && (
        <div style={{ position:"absolute", top:0, left: Math.min(hover/(days.length-1)*100, 70)+"%",
          background:C.surface3, border:`1px solid ${C.border2}`, borderRadius:8,
          padding:"6px 10px", fontSize:11, color:C.text, pointerEvents:"none", whiteSpace:"nowrap", zIndex:10 }}>
          <div style={{ color:C.text2, marginBottom:2 }}>{days[hover].label}</div>
          <div style={{ color:C.purple, fontWeight:700 }}>{fmt(days[hover].spent||0)} spent</div>
        </div>
      )}
    </div>
  );
}

// ── DONUT CHART ──────────────────────────────────────────────
function polarToCartesian(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function slicePath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const sweep = endDeg - startDeg;
  if (sweep <= 0) return "";
  const GAP = sweep > 6 ? 2 : 0;
  const s = startDeg + GAP / 2;
  const e = endDeg - GAP / 2;
  const large = e - s > 180 ? 1 : 0;
  const o1 = polarToCartesian(cx, cy, outerR, s);
  const o2 = polarToCartesian(cx, cy, outerR, e);
  const i1 = polarToCartesian(cx, cy, innerR, e);
  const i2 = polarToCartesian(cx, cy, innerR, s);
  return `M${o1.x},${o1.y} A${outerR},${outerR} 0 ${large} 1 ${o2.x},${o2.y} L${i1.x},${i1.y} A${innerR},${innerR} 0 ${large} 0 ${i2.x},${i2.y} Z`;
}

// Jewel tones — deep, rich, saturated. Glow on dark backgrounds without being neon.
const DONUT_COLORS = [
  "#8b5cf6", // amethyst
  "#22c55e", // emerald
  "#f97316", // deep orange
  "#3b82f6", // sapphire
  "#d946ef", // fuchsia
  "#64748b", // slate (Other)
];

function DonutChart({ transactions, month }) {
  const expenses = transactions.filter(t => t.date.startsWith(month) && t.amount < 0);
  const total = expenses.reduce((s, t) => s + Math.abs(t.amount), 0);
  if (total === 0 || expenses.length === 0) return null;

  const byCat = {};
  expenses.forEach(t => {
    const id = t.expenseCategory || "Other";
    byCat[id] = (byCat[id] || 0) + Math.abs(t.amount);
  });
  const sorted = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  let slices = sorted.slice(0, 5).map(([id, amt], i) => ({
    id, amt, pct: amt / total, color: DONUT_COLORS[i],
  }));
  if (sorted.length > 5) {
    const otherAmt = sorted.slice(5).reduce((s, [, v]) => s + v, 0);
    slices.push({ id: "Other", amt: otherAmt, pct: otherAmt / total, color: DONUT_COLORS[5] });
  }

  let angle = 0;
  const built = slices.map(s => {
    const start = angle;
    angle += s.pct * 360;
    return { ...s, start, end: angle };
  });

  const cx = 100, cy = 100, outerR = 85, innerR = 56;

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display:"flex", alignItems:"center", gap:20 }}>
        {/* Donut */}
        <div style={{ position:"relative", width:160, height:160, flexShrink:0 }}>
          {/* Ambient glow behind the donut */}
          <div style={{
            position:"absolute", inset:0, borderRadius:"50%",
            background:`radial-gradient(circle, ${built[0]?.color}28 0%, transparent 68%)`,
            filter:"blur(14px)",
            opacity:0, animation:"fadeIn 0.5s ease 0.3s forwards",
          }} />
          <svg viewBox="0 0 200 200" width="160" height="160" style={{ position:"relative" }}>
            <defs>
              <filter id="sliceGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={built[0]?.color || DONUT_COLORS[0]} floodOpacity="0.6" />
              </filter>
            </defs>
            {built.map((s, i) => (
              <path key={s.id} d={slicePath(cx, cy, outerR, innerR, s.start, s.end)}
                fill={s.color}
                filter={i === 0 ? "url(#sliceGlow)" : undefined}
                style={{ opacity:0, animation:`fadeIn 0.35s ease ${i * 60}ms forwards` }}
              />
            ))}
            <circle cx={cx} cy={cy} r={innerR - 1} fill="#0e0e13" />
          </svg>
          <div style={{ position:"absolute", inset:0, display:"flex",
            flexDirection:"column", alignItems:"center", justifyContent:"center",
            pointerEvents:"none" }}>
            <div style={{ fontSize:14, fontWeight:800, color:C.text, letterSpacing:"-0.02em" }}>
              {fmt(total)}
            </div>
            <div style={{ fontSize:9, color:C.text3, fontWeight:600,
              letterSpacing:"0.06em", textTransform:"uppercase", marginTop:1 }}>
              this month
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:10 }}>
          {built.map((s, i) => (
            <div key={s.id} style={{ display:"flex", alignItems:"center", gap:8,
              opacity:0, animation:`fadeIn 0.3s ease ${i * 60 + 180}ms forwards` }}>
              <div style={{ width:8, height:8, borderRadius:2, background:s.color,
                flexShrink:0, boxShadow:`0 0 5px ${s.color}99` }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <span style={{ fontSize:12, color:C.text, fontWeight:600,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {s.id}
                  </span>
                  <span style={{ fontSize:11, color:s.color, fontWeight:700, marginLeft:4, flexShrink:0 }}>
                    {Math.round(s.pct * 100)}%
                  </span>
                </div>
                <div style={{ height:2, background:"rgba(255,255,255,0.06)",
                  borderRadius:99, marginTop:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:99, background:s.color,
                    width:`${Math.round(s.pct * 100)}%`,
                    "--bar-w": `${Math.round(s.pct * 100)}%`,
                    animation:`barGrow 0.5s ease ${i * 60 + 260}ms both` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { SpendingChart, CalendarView, WeeklyChart, DonutChart };
